import { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { sinTildes } from './organizacionesLogica'

const MAX = 200          // options drawn at once; typing narrows the rest

// Picks one option from a long list by typing (every word, accents and case
// aside, in the label or the detail). Comercial's own, after SelectorProyecto:
// Comercial doesn't share components with other modules. The list floats over
// everything (a portal), so a side panel's scroll never clips it.
//   opciones: [{ value, label, detalle? }]; value: the chosen one's value (null = none)
//   onChange(value | null); textoVacio: the option that clears it (none if omitted)
//   crear: { etiqueta(texto, existe), onCrear(texto) } -- a last option that creates
//          a new one (always offered; `texto` is what was typed, '' when it already
//          exists exactly or nothing was typed)
export default function ComercialBuscador({
  opciones, value, onChange, placeholder = '', textoVacio, sinCoincidencias = '—', className = '', disabled, crear, ...resto
}) {
  const [texto, setTexto] = useState('')
  const [abierto, setAbierto] = useState(false)
  const [activo, setActivo] = useState(0)
  const [caja, setCaja] = useState(null)
  const ref = useRef(null)

  const elegido = (opciones || []).find(o => String(o.value) === String(value))
  const visibles = useMemo(() => {
    const palabras = sinTildes(texto).split(/\s+/).filter(Boolean)
    const todas = palabras.length
      ? (opciones || []).filter(o => {
        const en = sinTildes(`${o.label} ${o.detalle || ''}`)
        return palabras.every(p => en.includes(p))
      })
      : (opciones || [])
    return todas.slice(0, MAX)
  }, [opciones, texto])

  const medir = useCallback(() => {
    const r = ref.current?.getBoundingClientRect()
    if (!r) return
    const ancho = Math.min(Math.max(r.width, 280), window.innerWidth - 16)
    setCaja({ left: Math.max(8, Math.min(r.left, window.innerWidth - ancho - 8)), top: r.bottom + 2, width: ancho })
  }, [])

  useEffect(() => {
    if (!abierto) return undefined
    window.addEventListener('scroll', medir, true)
    window.addEventListener('resize', medir)
    return () => { window.removeEventListener('scroll', medir, true); window.removeEventListener('resize', medir) }
  }, [abierto, medir])

  const abrir = () => { if (disabled) return; setTexto(''); setActivo(0); medir(); setAbierto(true) }
  const elegir = (o) => { onChange(o ? o.value : null); setAbierto(false); setTexto('') }
  // "Create": what was typed, unless nothing was or an option has exactly that name.
  const escrito = texto.trim()
  const existe = !!escrito && (opciones || []).some(o => sinTildes(o.label).trim() === sinTildes(escrito))
  const creable = crear ? (existe ? '' : escrito) : ''
  const iCrear = crear ? visibles.length : -1                // the create option comes after the list
  const hacerCrear = () => { setAbierto(false); setTexto(''); crear.onCrear(creable) }

  const teclas = (e) => {
    const ultimo = visibles.length - 1 + (crear ? 1 : 0)
    if (e.key === 'ArrowDown') { e.preventDefault(); if (!abierto) abrir(); else setActivo(a => Math.min(ultimo, a + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActivo(a => Math.max(0, a - 1)) }
    else if (e.key === 'Enter' && abierto) {
      e.preventDefault()
      if (activo === iCrear) hacerCrear()
      else if (visibles[activo]) elegir(visibles[activo])
    } else if (e.key === 'Escape' && abierto) { e.stopPropagation(); setAbierto(false); setTexto('') }   // not the panel's Esc
  }

  return (
    <>
      <input ref={ref} autoComplete="off" role="combobox" aria-expanded={abierto} disabled={disabled}
        className={`w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-200 disabled:bg-gray-50 disabled:text-gray-500 ${className}`}
        value={abierto ? texto : (elegido?.label || '')} placeholder={abierto ? (elegido?.label || placeholder) : placeholder}
        onFocus={abrir} onClick={() => { if (!abierto) abrir() }}
        onChange={e => { setTexto(e.target.value); setActivo(0); if (!abierto) { medir(); setAbierto(true) } }}
        onBlur={() => setTimeout(() => { setAbierto(false); setTexto('') }, 150)} onKeyDown={teclas} {...resto} />
      {abierto && caja && createPortal(
        <div style={{ position: 'fixed', left: caja.left, top: caja.top, width: caja.width, zIndex: 60 }}
          className="max-h-72 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg text-sm">
          {textoVacio && value != null && value !== '' && (
            <div onMouseDown={e => { e.preventDefault(); elegir(null) }}
              className="px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50 cursor-pointer border-b border-gray-100">{textoVacio}</div>
          )}
          {visibles.length === 0 && <div className="px-3 py-3 text-xs text-gray-400">{sinCoincidencias}</div>}
          <ul role="listbox">
            {visibles.map((o, i) => (
              <li key={o.value} role="option" aria-selected={String(o.value) === String(value)}
                onMouseDown={e => { e.preventDefault(); elegir(o) }} onMouseEnter={() => setActivo(i)}
                ref={el => { if (el && i === activo) el.scrollIntoView({ block: 'nearest' }) }}
                className={`px-3 py-1.5 cursor-pointer ${i === activo ? 'bg-gray-100' : ''}`}>
                <span className={`block truncate text-gray-900 ${String(o.value) === String(value) ? 'font-medium' : ''}`}>{o.label}</span>
                {o.detalle && <span className="block truncate text-xs text-gray-500">{o.detalle}</span>}
              </li>
            ))}
          </ul>
          {crear && (
            <div role="option" aria-selected={false} onMouseDown={e => { e.preventDefault(); hacerCrear() }}
              onMouseEnter={() => setActivo(iCrear)}
              ref={el => { if (el && activo === iCrear) el.scrollIntoView({ block: 'nearest' }) }}
              className={`px-3 py-2 cursor-pointer border-t border-gray-100 font-medium text-sky-700 ${activo === iCrear ? 'bg-sky-50' : 'hover:bg-sky-50'}`}>
              {crear.etiqueta(creable, existe)}
            </div>
          )}
        </div>, document.body)}
    </>
  )
}
